import { forwardRef, useId } from 'react'
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { cn } from '@/utils/cn'
import { ChevronDownIcon } from '@/components/icons'

/**
 * 表单控件。
 *
 * 三件事在这里统一做掉，省得每个表单各写一遍还写不全：
 *   1. `label` 与控件通过 `id` 关联 —— 点标签能聚焦控件，读屏器能念出字段名；
 *   2. 错误文案用 `aria-describedby` + `aria-invalid` 关联，而不是只染个红边；
 *   3. 后端的字段级错误是 JSON Pointer（`FieldError.pointer`），页面把它翻成
 *      `error` 字符串传进来即可，控件不关心来源。
 */

const CONTROL_BASE =
  'w-full rounded-card border bg-surface text-foreground transition-colors duration-150 placeholder:text-subtle-foreground disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-muted-foreground'

const CONTROL_TONE = {
  normal: 'border-border hover:border-border-strong focus:border-primary',
  invalid: 'border-danger focus:border-danger',
} as const

export interface FieldProps {
  label: string
  /** 隐藏可见标签但保留给读屏器（例如顶栏搜索框，设计稿里没有标签）。 */
  hideLabel?: boolean
  hint?: string
  error?: string
  required?: boolean
  children: (attributes: {
    id: string
    'aria-invalid': boolean | undefined
    'aria-describedby': string | undefined
  }) => ReactNode
  className?: string
}

/** 通用字段外壳：标签 + 控件 + 提示/错误。 */
export function Field({
  label,
  hideLabel = false,
  hint,
  error,
  required,
  children,
  className,
}: FieldProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  // 有错误时优先关联错误文案；两者都在时一起念，顺序按 DOM。
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ')

  return (
    <div className={cn('space-y-1.5', className)}>
      <label
        htmlFor={id}
        className={cn(
          'block text-[0.8125rem] font-medium text-muted-foreground',
          hideLabel && 'sr-only',
        )}
      >
        {label}
        {required && (
          <span className="ml-0.5 text-danger" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy || undefined,
      })}
      {hint && !error && (
        <p id={hintId} className="text-xs leading-5 text-subtle-foreground">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs leading-5 text-danger">
          {error}
        </p>
      )}
    </div>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
  /** 左侧图标（搜索框）。会自动留出内边距。 */
  leadingIcon?: ReactNode
  trailingSlot?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid = false, leadingIcon, trailingSlot, className, ...props },
  ref,
) {
  const control = (
    <input
      ref={ref}
      className={cn(
        CONTROL_BASE,
        invalid ? CONTROL_TONE.invalid : CONTROL_TONE.normal,
        'h-10 px-3 text-sm',
        leadingIcon && 'pl-9',
        trailingSlot && 'pr-10',
        className,
      )}
      {...props}
    />
  )
  if (!leadingIcon && !trailingSlot) return control
  return (
    <div className="relative">
      {leadingIcon && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle-foreground">
          {leadingIcon}
        </span>
      )}
      {control}
      {trailingSlot && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2">{trailingSlot}</span>
      )}
    </div>
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid = false, className, rows = 4, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        CONTROL_BASE,
        invalid ? CONTROL_TONE.invalid : CONTROL_TONE.normal,
        'resize-y px-3 py-2.5 text-sm leading-6',
        className,
      )}
      {...props}
    />
  )
})

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean
}

/**
 * 下拉选择。
 *
 * 用原生 `<select>` 而不是自绘：移动端能拿到系统选择器（体验明显更好），
 * 键盘与读屏器行为免费正确。代价是箭头要自己画（`appearance-none` + 绝对定位）。
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid = false, className, children, ...props },
  ref,
) {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          CONTROL_BASE,
          invalid ? CONTROL_TONE.invalid : CONTROL_TONE.normal,
          'h-10 cursor-pointer appearance-none pl-3 pr-9 text-sm',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-subtle-foreground" />
    </div>
  )
})

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode
}

/**
 * 复选框。
 *
 * 保留真实 `<input type="checkbox">`（`sr-only`），视觉由旁边的方块承担，
 * 靠 `peer-checked:` 联动。这样键盘 Tab / 空格、表单提交、读屏器全部原生可用。
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, className, ...props },
  ref,
) {
  return (
    <label className={cn('group flex cursor-pointer items-center gap-2.5', className)}>
      <input ref={ref} type="checkbox" className="peer sr-only" {...props} />
      <span className="flex size-5 shrink-0 items-center justify-center rounded-md border border-border-strong bg-surface transition-colors peer-checked:border-primary peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background">
        <svg
          viewBox="0 0 24 24"
          className="size-3.5 scale-75 text-primary-foreground opacity-0 transition-all peer-checked:scale-100 peer-checked:opacity-100 group-has-[:checked]:scale-100 group-has-[:checked]:opacity-100"
          fill="none"
          stroke="currentColor"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m5 12.5 4.5 4.5 9.5-10" />
        </svg>
      </span>
      <span className="text-sm text-foreground">{label}</span>
    </label>
  )
})
